import * as Yup from 'yup'

// Shared "To date can't be before From date" rule for every date-range filter
// and scheduling form in the portal. The pairs go by several names across the
// app (from/to, from_date/to_date, created_from_date/created_to_date,
// startDate/endDate, starts_at/ends_at), so the check is expressed over plain
// values rather than a fixed field naming.
export const DATE_RANGE_ERROR = "To date can't be earlier than from date"

const toTime = (value) => {
  if (!value) return null
  const time = new Date(value).getTime()
  return Number.isNaN(time) ? null : time
}

// A range is only wrong when both ends are set and they cross. One end left
// blank is a legitimate open-ended filter, and the same day on both ends is a
// legitimate single-day filter, so neither counts as an error.
export const isDateRangeInvalid = (from, to) => {
  const start = toTime(from)
  const end = toTime(to)
  return start !== null && end !== null && end < start
}

// Returns the message to show under the To field, or '' when the pair is fine.
export const validateDateRange = (from, to, message = DATE_RANGE_ERROR) =>
  isDateRangeInvalid(from, to) ? message : ''

// Same check over several pairs at once, for pages with more than one range
// (e.g. bookings filters by booking date *and* created date). Returns a map of
// key -> message holding only the pairs that are actually wrong.
export const validateDateRanges = (pairs) =>
  Object.entries(pairs).reduce((errors, [key, { from, to, message }]) => {
    const error = validateDateRange(from, to, message || DATE_RANGE_ERROR)
    if (error) errors[key] = error
    return errors
  }, {})

// For filters that pass { from, to } straight to the API.
export const dateFilterSchema = Yup.object().shape({
  from: Yup.date().optional(),
  to: Yup.date()
    .when('from', (from, schema) =>
      from ? schema.min(from, DATE_RANGE_ERROR) : schema,
    )
    .optional(),
})

// Date inputs hand back '' when cleared, which Yup.date() would try to cast and
// reject as an invalid date. Both rules below read a blank as "not set".
const emptyToNull = (value, original) => (original === '' ? null : value)

// The start half of an optional range, e.g. Yup.object({
//   starts_at: optionalDate(), ends_at: endDateAfter('starts_at') }).
// The start field has to be declared for `when` to resolve against it.
export const optionalDate = () =>
  Yup.date().nullable().transform(emptyToNull)

// The end half: same as optionalDate, plus "not before the start field".
export const endDateAfter = (startField, message = DATE_RANGE_ERROR) =>
  optionalDate().when(startField, (start, schema) =>
    start ? schema.min(start, message) : schema,
  )
